import { useEffect } from 'react';
import { usePrevious } from '~/_shared/utils/hooks/usePrevious';
import { areSpreadsheetRowIdsEqual } from '~/_shared/utils/spreadsheet/generalSpreadsheet.helpers';
import { useProximities } from '~/proximity/useProximities';
import { useZoomToProximity } from '~/proximity/useZoomToProximity';
import { useActiveProximitySelector } from '~/store/frontendState/activeMapElements/activeMapElements.selectors';
import { useMapComponentLastBoundsSelector } from '~/store/frontendState/mapComponent/mapComponent.selectors';
import { useProximityLocation } from './useProximityLocation';

/*
  When user selects a proximity but it's border is not visible due to zoom level
  We zoom out to so the whole proximity is visible
*/
export const useZoomOutActivatedProximity = () => {
  const activeProximityInfo = useActiveProximitySelector();
  const previousActiveProximityInfo = usePrevious(activeProximityInfo);
  const proximities = useProximities();
  const mapBounds = useMapComponentLastBoundsSelector()?.bounds;
  const { isProximityAtLocation } = useProximityLocation();
  const { zoomToProximity } = useZoomToProximity();

  useEffect(() => {
    if (
      activeProximityInfo &&
      // only execute the moment the active proximity changes
      (!previousActiveProximityInfo
        || previousActiveProximityInfo.proximityId !== activeProximityInfo.proximityId)
        || (activeProximityInfo?.spreadsheetRowId && !areSpreadsheetRowIdsEqual(
          previousActiveProximityInfo?.spreadsheetRowId,
          activeProximityInfo.spreadsheetRowId
        ))
    ) {
      const proximity = proximities.find(proximity => proximity.id === activeProximityInfo?.proximityId) ?? null;
      if (proximity && mapBounds) {
        const proximityAtSouthWest = isProximityAtLocation(mapBounds.getSouthWest(), proximity, activeProximityInfo?.spreadsheetRowId);
        const proximityAtNorthEast = isProximityAtLocation(mapBounds.getNorthEast(), proximity, activeProximityInfo?.spreadsheetRowId);
        const proximityAtNorthWest = isProximityAtLocation(
          { lat: mapBounds.getNorthEast().lat, lng: mapBounds.getSouthWest().lng },
          proximity,
          activeProximityInfo?.spreadsheetRowId,
        );
        const proximityAtSouthEast = isProximityAtLocation(
          { lat: mapBounds.getSouthWest().lat, lng: mapBounds.getNorthEast().lng },
          proximity,
          activeProximityInfo?.spreadsheetRowId,
        );

        if (
          proximityAtSouthWest &&
          proximityAtNorthEast &&
          proximityAtNorthWest &&
          proximityAtSouthEast
        ) {
          zoomToProximity(proximity, activeProximityInfo?.spreadsheetRowId);
        }
      }
    }
  }, [activeProximityInfo, previousActiveProximityInfo, proximities, mapBounds, isProximityAtLocation, zoomToProximity]);
};
